import { FC } from 'react';
import { Box, Button, Dialog, DialogContent, Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import { useLocalization } from 'Hooks';
import { DialogCloseComponent } from 'Components';

require('./StudentCardComparisonDialog.scss');

type ComparisonRow = {
  label: string;
  plusValue: string;
  studentValue: string;
};

type StudentCardComparisonDialogProps = {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  onContinuePlus: () => void;
  onContinueStudent: () => void;
};

const StudentCardComparisonDialog: FC<StudentCardComparisonDialogProps> = ({
  open,
  onClose,
  onCancel,
  onContinuePlus,
  onContinueStudent,
}) => {
  const localization = useLocalization();

  // Helper function to render values with color coding
  const renderValueWithColor = (value: string) => {
    const isVar = value === 'Var';
    const isYok = value === 'Yok';
    
    if (isVar) {
      return <span className="studentCardComparisonDialog__varValue">{value}</span>;
    }
    if (isYok) {
      return <span className="studentCardComparisonDialog__yokValue">{value}</span>;
    }
    return value;
  };

  const rows: ComparisonRow[] = [
    {
      label: localization.web_pc_istanbulcard_student_compare_popup_row_1_label,
      plusValue: localization.web_pc_istanbulcard_student_compare_popup_row_1_plus_value,
      studentValue: localization.web_pc_istanbulcard_student_compare_popup_row_1_student_value,
    },
    {
      label: localization.web_pc_istanbulcard_student_compare_popup_row_2_label,
      plusValue: localization.web_pc_istanbulcard_student_compare_popup_row_2_plus_value,
      studentValue: localization.web_pc_istanbulcard_student_compare_popup_row_2_student_value,
    },
    {
      label: localization.web_pc_istanbulcard_student_compare_popup_row_3_label,
      plusValue: localization.web_pc_istanbulcard_student_compare_popup_row_3_plus_value,
      studentValue: localization.web_pc_istanbulcard_student_compare_popup_row_3_student_value,
    },
    {
      label: localization.web_pc_istanbulcard_student_compare_popup_row_4_label,
      plusValue: localization.web_pc_istanbulcard_student_compare_popup_row_4_plus_value,
      studentValue: localization.web_pc_istanbulcard_student_compare_popup_row_4_student_value,
    },
    {
      label: localization.web_pc_istanbulcard_student_compare_popup_row_5_label,
      plusValue: localization.web_pc_istanbulcard_student_compare_popup_row_5_plus_value,
      studentValue: localization.web_pc_istanbulcard_student_compare_popup_row_5_student_value,
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="studentCardComparisonDialog">
      <DialogCloseComponent closeFunction={onClose} />
      <DialogContent className="studentCardComparisonDialog__content">
        <Box className="studentCardComparisonDialog__tableWrap">
          <Table className="studentCardComparisonDialog__table" size="small">
            <TableBody>
              <TableRow className="studentCardComparisonDialog__cardHeaderRow">
                <TableCell className="studentCardComparisonDialog__labelCell" />
                <TableCell className="studentCardComparisonDialog__valueCell studentCardComparisonDialog__cardHeaderCell">
                  <Box className="studentCardComparisonDialog__cardHeaderContent">
                    <img
                      className="studentCardComparisonDialog__cardImage"
                      src={localization.web_pc_istanbulcard_student_compare_popup_plus_image}
                      alt={localization.web_pc_istanbulcard_student_compare_popup_plus_title}
                    />
                    <Typography variant="h6" className="studentCardComparisonDialog__cardTitle" style={{ fontSize: 14 }}>
                      {localization.web_pc_istanbulcard_student_compare_popup_plus_title}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell className="studentCardComparisonDialog__valueCell studentCardComparisonDialog__cardHeaderCell">
                  <Box className="studentCardComparisonDialog__cardHeaderContent">
                    <img
                      className="studentCardComparisonDialog__cardImage"
                      src={localization.web_pc_istanbulcard_student_compare_popup_student_image}
                      alt={localization.web_pc_istanbulcard_student_compare_popup_student_title}
                    />
                    <Typography variant="h6" className="studentCardComparisonDialog__cardTitle" style={{ fontSize: 14 }}>
                      {localization.web_pc_istanbulcard_student_compare_popup_student_title}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              {rows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell className="studentCardComparisonDialog__labelCell">{row.label}</TableCell>
                  <TableCell className="studentCardComparisonDialog__valueCell">{renderValueWithColor(row.plusValue)}</TableCell>
                  <TableCell className="studentCardComparisonDialog__valueCell">{renderValueWithColor(row.studentValue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box className="studentCardComparisonDialog__warning">
          {localization.web_pc_istanbulcard_student_compare_popup_warning_text}
        </Box>

        <Box className="studentCardComparisonDialog__actions">
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={onContinuePlus}
            className="studentCardComparisonDialog__plusBtn"
          >
            {localization.web_pc_istanbulcard_student_compare_popup_plus_button}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={onContinueStudent}
            className="studentCardComparisonDialog__studentBtn"
          >
            {localization.web_pc_istanbulcard_student_compare_popup_student_button}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={onCancel}
            className="studentCardComparisonDialog__cancelBtn"
          >
            {localization.web_pc_istanbulcard_student_compare_popup_cancel_button}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StudentCardComparisonDialog;